export const defaultStoryContextOptions = { mode: 'staged', inventory: 'selected', routines: 'overview',
  history: 'selected', inputBudgetTokens: 256000, focusIds: [] };

export function StoryContextMetrics({ metrics }) {
  if (!metrics) return null;
  return <div aria-label="Context size estimate">
    <p>Estimated context: {metrics.estimatedTokens.toLocaleString()} tokens ({(metrics.bytes / 1024).toFixed(0)} KB).
      {' '}This estimate excludes instructions and response schemas. Exact input counts are checked before each AI request.</p>
    <table><thead><tr><th>Section</th><th>Estimated tokens</th><th>KB</th></tr></thead>
      <tbody>{metrics.sections.map(section => <tr key={section.section}><td>{section.section}</td>
        <td>{section.estimatedTokens.toLocaleString()}</td><td>{(section.bytes / 1024).toFixed(0)}</td></tr>)}</tbody></table>
  </div>;
}

export default function StoryContextControls({ label, options, onChange, preview, choices, onPreview, canPreview }) {
  const change = (field, value) => onChange({ ...options, [field]: value });
  return <details open aria-label={`${label} planning context`}><summary>{label} planning context and size</summary>
    <p>Keep the whole town discoverable, then load relevant details. Pinned people and places receive full identity and motive details.
      Detail settings below control inventory, schedules and history.</p>
    <label>Context loading <select value={options.mode} onChange={event => change('mode', event.target.value)}>
      <option value="staged">Directory, then selected details</option><option value="full">Full records (stage automatically if too large)</option>
    </select></label>
    <label>Inventory detail <select value={options.inventory} onChange={event => change('inventory', event.target.value)}>
      <option value="identities">Identities and ownership</option><option value="selected">Selected item descriptions</option><option value="full">All item descriptions</option>
    </select></label>
    <label>Routine detail <select value={options.routines} onChange={event => change('routines', event.target.value)}>
      <option value="overview">Overview per person</option><option value="selected">Full schedules for selected people</option><option value="full">All schedules</option>
    </select></label>
    <label>History detail <select value={options.history} onChange={event => change('history', event.target.value)}>
      <option value="summaries">Event summaries</option><option value="selected">Selected event accounts</option><option value="full">All event accounts</option>
    </select></label>
    <label>Input token budget <input type="number" min="16000" max="900000" step="1000" value={options.inputBudgetTokens}
      onChange={event => change('inputBudgetTokens', Number(event.target.value))} /></label>
    <p>Up to 128,000 additional tokens are reserved for each response, including large population and equipment plans. Omitted details remain saved and are used for validation.</p>
    <button type="button" onClick={onPreview}>Load Context Preview</button>
    {!canPreview && <p>Select a saved Place Tree in this panel to load its context.</p>}
    {choices.length > 0 && <label>Pinned people and places (optional, up to 40)
      <select multiple size={8} value={options.focusIds} onChange={event => change('focusIds', Array.from(event.target.selectedOptions, option => option.value))}>
        {choices.map(choice => <option key={choice.id} value={choice.id}>{choice.kind}: {choice.name || choice.id}</option>)}
      </select></label>}
    <StoryContextMetrics metrics={preview?.metrics} />
    {preview && <p>Preview source: {preview.target.database} ({preview.target.profile}).</p>}
  </details>;
}

export function StoryContextDiagnostics({ diagnostics }) {
  if (!diagnostics) return null;
  return <details><summary>Planning context used</summary>
    {diagnostics.automaticStaging && <p>Full detail exceeded the input budget. Planning switched to the directory and selected details.</p>}
    <p>{diagnostics.expandedRecordCount ?? diagnostics.expandedIds.length} records expanded; {diagnostics.selectionRounds || 0} selection rounds.</p>
    <StoryContextMetrics metrics={diagnostics.preview} />
    <ul>{diagnostics.requests.map((request, index) => <li key={index}>{request.operation}: {request.inputTokens.toLocaleString()} input tokens
      {' '}({request.allowed ? 'within budget' : 'over budget; generation withheld'}).</li>)}</ul>
  </details>;
}
